import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import type { LocationUserTableRow, LocationRole } from '@/types/location-users'
import { LOCATION_ROLES, LOCATION_ROLE_LABELS, LOCATION_ROLE_DESCRIPTIONS } from '@/types/location-users'

interface EditLocationRoleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: LocationUserTableRow | null
  onUpdateRole: (newRole: LocationRole) => Promise<void>
  isLoading?: boolean
}

export function EditLocationRoleDialog({
  open,
  onOpenChange,
  user,
  onUpdateRole,
  isLoading = false,
}: EditLocationRoleDialogProps) {
  const [role, setRole] = useState<LocationRole>(user?.location_role || 'viewer')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update role when user changes
  useEffect(() => {
    if (user) {
      setRole(user.location_role)
    }
  }, [user])

  const hasChanged = role !== user?.location_role

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!hasChanged || !user) return

    setIsSubmitting(true)
    try {
      await onUpdateRole(role)
      onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!user) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update User Role</DialogTitle>
          <DialogDescription>
            Change the role for {user.user_display_name || user.user_email}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>User</Label>
            <div className="flex flex-col gap-1">
              <p className="font-medium text-sm">{user.user_display_name || 'Unnamed'}</p>
              <p className="text-xs text-muted-foreground">{user.user_email}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">New Role</Label>
            <Select
              value={role}
              onValueChange={(value) => setRole(value as LocationRole)}
              disabled={isSubmitting || isLoading}
            >
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCATION_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {LOCATION_ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {LOCATION_ROLE_DESCRIPTIONS[role]}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting || isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!hasChanged || isSubmitting || isLoading}
            >
              {isSubmitting ? 'Updating...' : 'Update Role'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
